<script setup>
import { computed } from 'vue';
import InputMediaBankEntry from '../../forms/inputMediaBankEntry.vue';
import { componentCatalog, isFieldVisible } from './component-catalog';

// Mirrors the layout of page-builder/block-editor/blocks/buttonUnified.vue, limited to the
// options the AI page builder supports. The catalog entry stays the source of truth for
// which options exist and what their defaults are.

const props = defineProps({
    modelValue: { type: Object, required: true },
});

// Emits only the changed keys so the parent merges them into its current values;
// spreading modelValue here could drop a patch emitted before props caught up.
const emit = defineEmits(['patch']);

const configFields = componentCatalog.find((component) => component.type === 'button-unified').configFields;

const fieldOptions = (key) => configFields.find((field) => field.key === key)?.options ?? [];

const update = (patch) => {
    emit('patch', patch);
};

const modelField = (key) => computed({
    get: () => props.modelValue[key],
    set: (value) => update({ [key]: value }),
});

const label = modelField('label');
const buttonStyle = modelField('button_style');
const alignment = modelField('alignment');
const rounding = modelField('rounding');
const targetData = modelField('target_data');
const targetOpenInNewTab = modelField('target_open_in_new_tab');

const targetTypeLabels = {
    url: () => 'URL',
    email: () => _mft('shared:email'),
    page: () => _mft('page:domainNameSingular'),
    file: () => _mft('pagebuilderBlock:unifiedButton.input.buttonFile.label'),
};

const targetTypeOptions = computed(() => fieldOptions('target_type').map((name) => ({
    name,
    label: targetTypeLabels[name]?.() ?? name,
})));

const buttonStyleOptions = computed(() => fieldOptions('button_style').map((name) => ({
    name,
    label: _mft(`pagebuilderBlock:general.input.style.${name}`),
})));

const alignmentOptions = computed(() => fieldOptions('alignment').map((name) => ({
    name,
    label: _mft(`pagebuilderBlock:general.input.alignment.${name}`),
})));

const roundingOptions = computed(() => fieldOptions('rounding').map((name) => ({
    name,
    label: _mft(`pagebuilderBlock:general.input.rounding.${name}`),
})));

const paddingSizeOptions = computed(() => fieldOptions('padding_size').map((name) => ({
    name,
    label: _mft(`pagebuilderBlock:general.input.size.${name}`),
})));

const colorModeOptions = computed(() => fieldOptions('color_mode').map((name) => ({
    name,
    label: _mft(`page:aiPageBuilder.components.button.colorMode.${name}`),
})));

const openInNewTabField = configFields.find((field) => field.key === 'target_open_in_new_tab');
const showOpenInNewTab = computed(() => isFieldVisible(openInNewTabField, props.modelValue));

// target_data holds a different shape per link type, so it is cleared on change.
const setTargetType = (option) => {
    if (option.name === props.modelValue.target_type) {
        return;
    }

    update({
        target_type: option.name,
        target_data: null,
        target_open_in_new_tab: option.name === 'file',
    });
};

const setTargetPage = (page) => {
    update({
        target_data: {
            slug: page.slug,
            title: page.title,
        },
    });
};
</script>

<template>
    <ui-tabbed
        :responsive="false"
        :track-state="false"
    >
        <ui-tabbed-tab
            :title="_mft('page:aiPageBuilder.components.button.tabs.content')"
            value="content"
            :active="true"
        >
            <input-text
                :label="_mft('pagebuilderBlock:unifiedButton.input.buttonLabel.label')"
                :required="true"
                v-model:content="label"
            />

            <input-label class="mb-1" :label="_mft('pagebuilderBlock:unifiedButton.input.buttonLinkType.label')" />
            <button-bar
                :options="targetTypeOptions"
                :selected-option-name="modelValue.target_type"
                @select="setTargetType"
                size="sm"
            />

            <div v-if="modelValue.target_type === 'page'" class="mb-2">
                <input-label :label="_mft('pagebuilderBlock:unifiedButton.input.buttonTargetPage.label')" :required="true" />
                <div
                    v-if="modelValue.target_data?.slug"
                    class="flex items-center justify-between space-x-4"
                >
                    <span class="text-gray-600">{{ modelValue.target_data.title }}</span>
                    <button-secondary
                        size="sm"
                        @click="targetData = null"
                    >{{ _mft('shared:action.change') }}</button-secondary>
                </div>
                <page-search
                    v-else
                    class="w-full"
                    :route="route('api.invoice-links.purchase-completed-page-candidates')"
                    :placeholder="_mft('form:form.redirect.search.placeholder')"
                    @page-selected="setTargetPage"
                />
            </div>

            <input-text
                v-if="modelValue.target_type === 'url'"
                :label="_mft('pagebuilderBlock:unifiedButton.input.buttonLink.label')"
                v-model:content="targetData"
            />

            <input-text
                v-if="modelValue.target_type === 'email'"
                :label="_mft('pagebuilderBlock:unifiedButton.input.buttonEmail.label')"
                v-model:content="targetData"
            />

            <input-media-bank-entry
                v-if="modelValue.target_type === 'file'"
                :label="_mft('pagebuilderBlock:unifiedButton.input.buttonFile.label')"
                type="file"
                :required="true"
                v-model:content="targetData"
            />

            <toggle-switch
                v-if="showOpenInNewTab"
                v-model:content="targetOpenInNewTab"
                :label="_mft('pagebuilderBlock:unifiedButton.input.buttonLinkTarget.openInNewPage.label')"
            />
        </ui-tabbed-tab>

        <ui-tabbed-tab
            :title="_mft('page:aiPageBuilder.components.button.tabs.design')"
            value="design"
        >
            <foldable-sidebar-container
                :title="_mft('page:aiPageBuilder.components.button.sections.button')"
                border-bottom
            >
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <input-label :label="_mft('pagebuilderBlock:unifiedButton.input.buttonStyle.label')" />
                        <input-select
                            :options="buttonStyleOptions"
                            labelSpecification="label"
                            valueSpecification="name"
                            v-model:content="buttonStyle"
                        />
                    </div>

                    <div>
                        <input-label :label="_mft('pagebuilderBlock:unifiedButton.input.alignment.label')" />
                        <input-select
                            :options="alignmentOptions"
                            labelSpecification="label"
                            valueSpecification="name"
                            v-model:content="alignment"
                        />
                    </div>
                </div>

                <div>
                    <input-label :label="_mft('pagebuilderBlock:general.input.rounding.label')" />
                    <input-select
                        :options="roundingOptions"
                        labelSpecification="label"
                        valueSpecification="name"
                        v-model:content="rounding"
                    />
                </div>
            </foldable-sidebar-container>

            <foldable-sidebar-container
                :title="_mft('pagebuilder:styleEditor.padding')"
                border-bottom
            >
                <button-bar
                    class="mb-4"
                    :options="paddingSizeOptions"
                    :selected-option-name="modelValue.padding_size"
                    @select="(option) => update({ padding_size: option.name })"
                    size="sm"
                />
            </foldable-sidebar-container>

            <foldable-sidebar-container
                :title="_mft('page:aiPageBuilder.components.button.sections.colors')"
            >
                <div class="mt-4">
                    <button-bar
                        :options="colorModeOptions"
                        :selected-option-name="modelValue.color_mode"
                        @select="(option) => update({ color_mode: option.name })"
                    />
                </div>

                <div v-if="modelValue.color_mode === 'custom'">
                    <input-label :label="_mft('pagebuilderBlock:unifiedButton.input.buttonColor.label')" />
                    <color-picker
                        :color="modelValue.cssBackgroundColor"
                        :debounce="100"
                        :show-palette="true"
                        format="hex"
                        @colorChanged="update({ cssBackgroundColor: $event })"
                    />

                    <input-label :label="_mft('pagebuilderBlock:unifiedButton.input.textColor.label')" />
                    <color-picker
                        :color="modelValue.cssColor"
                        :debounce="100"
                        :show-palette="true"
                        format="hex"
                        @colorChanged="update({ cssColor: $event })"
                    />
                </div>
            </foldable-sidebar-container>
        </ui-tabbed-tab>
    </ui-tabbed>
</template>
